import "./globals.css";
import { makeAutoObservable, runInAction } from "mobx";
import {
    adminGetAgents,
    adminGetArtifacts,
    adminGetDocuments,
    adminGetTags,
    getOrganisations,
    loginForAccessToken,
    logout as _logout,
    readUsersMe,
    getClients,
    setActiveAgent,
    getEvents,
    getUserPreferences,
    updateSpeakerProfile,
} from "@/api/sdk.gen";
import { client } from "@/api/client.gen";
import { tag_list_item } from "./typing/tags";
import { artifact_list_item } from "./typing/artifacts";
import { organisation_list_item } from "./typing/organisations";
import { agent_list_item } from "./typing/agents";
import { chat_list_item } from "./typing/chat";
import { calendar_event_list_item } from "./typing/events";
import { document_list_item } from "./typing/documents";
import { Request } from "@/components/CRUD";
import { AdminGetAgentsData, AdminGetArtifactsData, AdminGetDocumentsData, AdminGetTagsData, GetClientsData, GetEventsData, UpdateSpeakerProfileData } from "@/api/types.gen";
import {
    applyDocumentLocale,
    detectLocale,
    fetchStrings,
    persistLocale,
    persistUserLocale,
    setCatalog,
    t as translate,
    isLocale,
    type LocaleCode,
} from "@/i18n";

export type User = {
    id: number;
    full_name: string;
    email: string;
    active_agent_id: number;
    active_organisation: {
        id: number;
        permission_type: number;
    };
    is_super_admin: boolean;
    speaker_profile?: string[];
    locale?: LocaleCode;
};

export interface UserPreference {
    id: number;
    content: string;
    created_at: string;
    last_updated_at: string;
}

export class AppState {
    user: User | null = null;

    private navigateCallback: ((path: string) => void) | null = null;

    setNavigateCallback = (callback: (path: string) => void) => {
        this.navigateCallback = callback;
    };

    private navigate = (path: string) => {
        if (this.navigateCallback) {
            this.navigateCallback(path);
        }
    };

    theme: theme = "light";
    tags: tag_list_item[] = [];
    artifacts: artifact_list_item[] = [];
    artifacts_page: number = 0;
    agents: agent_list_item[] = [];
    organisations: organisation_list_item[] = [];
    clients: user_list_item[] = [];
    clients_page: number = 0;
    events: calendar_event_list_item[] = [];
    events_page: number = 0;
    documents: document_list_item[] = [];
    documents_page: number = 0;
    preferences: UserPreference[] = [];
    locale: LocaleCode = "da";
    strings: Record<string, string> = {};

    chat_refresh: number = 0;

    loadingAuth = false; // This one triggers complete tree rerender

    hydrated = false;
    modals: Array<{ title: string; body?: React.ReactNode; comment?: { name: string; val: string }; action?: () => void; close?: (() => void) | null }> = [];

    pageSize = 20;
    chats: chat_list_item[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    t = (key: string, vars?: Record<string, string | number>) => {
        void this.strings;
        return translate(key, vars);
    };

    loadStrings = async (locale: LocaleCode) => {
        const strings = await fetchStrings(locale);
        setCatalog(strings);
        runInAction(() => {
            this.locale = locale;
            this.strings = strings;
        });
        persistLocale(locale);
        applyDocumentLocale(locale, strings);
    };

    setLocale = async (locale: LocaleCode) => {
        if (locale === this.locale && Object.keys(this.strings).length > 0) {
            persistLocale(locale);
            return;
        }
        await this.loadStrings(locale);
        if (this.user) {
            try {
                await persistUserLocale(locale);
                runInAction(() => {
                    if (this.user) this.user.locale = locale;
                });
            } catch (e) {
                console.error("Failed to persist locale:", e);
            }
        }
    };

    loadAppStateObjects = async () => {
        this.getTags({ pageNumber: 0, search: "" });
        this.getArtifacts({ pageNumber: 0, search: "" });
        this.getOrgs();
        this.getAgents({ pageNumber: 0, search: "" });
        this.getPreferences();
        // Ensure calendar watches are active, then reload tags to pick up any renames
        client
            .post({ url: "/api/calendar/ensure-watches" })
            .then(() => {
                this.getTags({ pageNumber: 0, search: "" });
            })
            .catch((e: any) => console.warn("ensure-watches:", e));
    };

    setSpeakerProfile = Request<UpdateSpeakerProfileData, any, { speaker_profile: string[] }>(
        updateSpeakerProfile,
        (props) => ({ body: { speaker_profile: props.speaker_profile } }),
        () => {},
        (data: any) => {
            runInAction(() => {
                if (this.user) {
                    this.user.speaker_profile = data.speaker_profile;
                }
            });
        },
        (error) => {
            console.error("Failed to update speaker profile:", error);
        },
        (props) => true,
    );

    loadUser: () => void = async () => {
        this.loadingAuth = true;
        try {
            // First try to get user with current token
            const response = await readUsersMe();
            
            if (response.data) {
                // Success - we have a valid user
                this.loadAppStateObjects();
                this.user = response.data as User;
                const userLocale = this.user.locale;
                if (isLocale(userLocale) && userLocale !== this.locale) {
                    await this.loadStrings(userLocale);
                }
            } else {
                this.user = null;
            }
        } finally {
            this.loadingAuth = false;
        }
    };

    login: (username: string, password: string) => void = async (username: string, password: string) => {
        this.loadingAuth = true;
        try {
            // First attempt to login with credentials
            await loginForAccessToken({
                body: {
                    username,
                    password,
                },
            });

            // If login was successful, fetch the user data
            try {
                const userResponse = await readUsersMe();
                if (userResponse.data) {
                    this.user = userResponse.data as User;
                    const userLocale = this.user.locale;
                    if (isLocale(userLocale) && userLocale !== this.locale) {
                        await this.loadStrings(userLocale);
                    }
                    this.loadAppStateObjects();
                    this.navigate("/workspace");
                }
            } catch (userError) {
                console.error("Failed to fetch user after login:", userError);
                this.user = null;
                this.navigate("/signin");
            }
        } catch (loginError) {
            console.error("Login failed:", loginError);
            this.user = null;
            this.navigate("/signin");
        } finally {
            this.loadingAuth = false;
        }
    };

    logout: () => void = async () => {
        try {
            await _logout();
            this.user = null;
            this.navigate("/signin");
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    intialize = () => {
        const storedTheme = localStorage.getItem("theme") as theme;
        if (storedTheme) {
            this.theme = storedTheme;
        }
        const locale = detectLocale();
        this.locale = locale;
        this.loadStrings(locale)
            .catch((e) => console.error("Failed to load translations:", e))
            .finally(() => {
                runInAction(() => {
                    this.hydrated = true;
                });
                this.loadUser();
            });
    };
    setTheme = (t: theme) => {
        this.theme = t;
        localStorage.setItem("theme", t);
    };

    setModal = (modal: { title: string; body?: React.ReactNode; comment?: { name: string; val: string }; action?: () => void; close?: (() => void) | null } | undefined) => {
        this.modals = modal === undefined ? [] : [modal];
    };

    appendModal = (modal: { title: string; body?: React.ReactNode; comment?: { name: string; val: string }; action?: () => void; close?: (() => void) | null }) => {
        this.modals = [...this.modals, modal];
    };

    popModal = () => {
        this.modals = this.modals.slice(0, -1);
    };

    removeModal = (index: number) => {
        this.modals = this.modals.filter((_, i) => i !== index);
    };

    getOrgs = async () => {
        const response: any = await getOrganisations();
        runInAction(() => {
            if (response.data) {
                this.organisations = response.data;
            } else {
                this.organisations = [];
                if (response.error) {
                    console.error("Error occurred:", response.error);
                }
            }
        });
    };

    getAgents = Request<AdminGetAgentsData, any, { pageNumber: number; search: string }>(
        adminGetAgents,
        (props) => ({
            body: { page_size: 999999, page_number: props.pageNumber, search: props.search },
        }),
        () => {},
        (data: any) => {
            runInAction(() => {
                this.agents = data.agents as agent_list_item[];
            });
        },
        (error) => {
            runInAction(() => {
                this.agents = [];
            });
            console.error("Error occurred:", error);
        },
        () => true,
    );

    getTags = Request<AdminGetTagsData, any, { pageNumber: number; search: string }>(
        adminGetTags,
        (props) => ({
            body: { page_number: props.pageNumber, page_size: 999999, search: props.search },
        }),
        () => {},
        (data: any) => {
            runInAction(() => {
                this.tags = data.tags as tag_list_item[];
            });
        },
        (error) => {
            runInAction(() => {
                this.tags = [];
            });
            console.error("Error occurred:", error);
        },
        () => true,
    );

    artifacts_maxpage: number | null = null;

    getArtifacts = Request<AdminGetArtifactsData, any, { pageNumber: number; search: string; tagsFilter?: number[] }>(
        adminGetArtifacts,
        (props) => ({
            body: {
                page_number: props.pageNumber,
                page_size: this.pageSize,
                search: props.search,
                tags_filter: props.tagsFilter,
            },
        }),
        () => {},
        (data: any, props) => {
            runInAction(() => {
                const res = data as { artifacts: artifact_list_item[] };

                this.artifacts_page = props.pageNumber;

                if (res.artifacts.length < this.pageSize) this.artifacts_maxpage = props.pageNumber;
                else this.artifacts_maxpage = null;

                if (props.pageNumber != 0) this.artifacts = [...this.artifacts, ...res.artifacts];
                else this.artifacts = res.artifacts;
            });
        },
        (error) => {
            runInAction(() => {
                this.artifacts = [];
            });
            console.error("Error occurred:", error);
        },
        (props) => !(this.artifacts_maxpage && props.pageNumber >= this.artifacts_maxpage),
    );

    clients_maxpage: number | null = null;

    _getClients = Request<GetClientsData, any, { pageNumber: number; search?: string }>(
        getClients,
        (props) => ({
            body: {
                page_size: this.pageSize,
                page_number: props.pageNumber,
                search: props.search,
            },
        }),
        () => {},
        (data: any, props) => {
            runInAction(() => {
                const res = data as { users: user_list_item[] };

                this.clients_page = props.pageNumber;

                if (res.users.length < this.pageSize) this.clients_maxpage = props.pageNumber;
                else this.clients_maxpage = null;

                if (props.pageNumber != 0) this.clients = [...this.clients, ...res.users];
                else this.clients = res.users;
            });
        },
        (error) => {
            runInAction(() => {
                this.clients = [];
            });
            console.error("Error occurred:", error);
        },
        (props) => !(this.clients_maxpage && props.pageNumber >= this.clients_maxpage),
    );

    events_maxpage: number | null = null;

    getEvents = Request<GetEventsData, any, { pageNumber: number; search?: string }>(
        getEvents,
        (props) => ({
            body: {
                page_size: this.pageSize,
                page_number: props.pageNumber,
                search: props.search,
            },
        }),
        () => {},
        (data: any, props) => {
            runInAction(() => {
                const res = data as { events: calendar_event_list_item[] };

                this.events_page = props.pageNumber;

                if (res.events.length < this.pageSize) this.events_maxpage = props.pageNumber;
                else this.events_maxpage = null;

                if (props.pageNumber != 0) this.events = [...this.events, ...res.events];
                else this.events = res.events;
            });
        },
        (error) => {
            runInAction(() => {
                this.events = [];
            });
            console.error("Error occurred:", error);
        },
        (props) => !(this.events_maxpage && props.pageNumber >= this.events_maxpage),
    );

    documents_maxpage: number | null = null;

    getDocuments = Request<AdminGetDocumentsData, any, { pageNumber: number; search?: string; tagsFilter?: number[] }>(
        adminGetDocuments,
        (props) => ({
            body: {
                page_size: this.pageSize,
                page_number: props.pageNumber,
                search: props.search,
                tags_filter: props.tagsFilter,
            },
        }),
        () => {},
        (data: any, props) => {
            runInAction(() => {
                const res = data as { documents: document_list_item[] };

                this.documents_page = props.pageNumber;

                if (res.documents.length < this.pageSize) this.documents_maxpage = props.pageNumber;
                else this.documents_maxpage = null;

                if (props.pageNumber != 0) this.documents = [...this.documents, ...res.documents];
                else this.documents = res.documents;
            });
        },
        (error) => {
            runInAction(() => {
                this.documents = [];
            });
            console.error("Error occurred:", error);
        },
        (props) => !(this.documents_maxpage && props.pageNumber >= this.documents_maxpage),
    );

    getPreferences = Request<any, any, void>(
        getUserPreferences,
        () => ({}),
        () => {},
        (data: any) => {
            runInAction(() => {
                this.preferences = (data as { preferences: UserPreference[] })?.preferences || [];
            });
        },
        (error) => {
            runInAction(() => {
                this.preferences = [];
            });
            console.error("Could not get Preferences:", error);
        },
        () => true,
    );
}

// Only initialize appState on the client side
export const appState = new AppState();

type theme = "light" | "dark";
